import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { Autocomplete, Box, Paper } from '@mui/material'
import debounce from 'lodash/debounce'

import { apiZodios } from '~api/apiZodios'
import { type RootState } from '~redux/rootReducer'
import { typography } from '~tokens/internal'
import { type EmailAddressResult } from '~types'
import { isValidEmail } from '~utils/emailUtils'

import Avatar from '../Avatar'
import Chip from '../Chip'
import TextField from '../TextField'
import Typography from '../Typography'

import './EmailRecipientSelector.scss'

export interface RecipientGroup {
  label: string
  recipients: EmailAddressResult[]
}

export interface EmailRecipientSelectorProps {
  selectedRecipients: EmailAddressResult[]
  onRecipientsChange: (recipients: EmailAddressResult[]) => void
  suggestedGroups?: RecipientGroup[]
  onPendingChange?: (pending: boolean) => void
  placeholder?: string
  organizationIdentityId?: string
  maxRecipients?: number
}

const EmailRecipientSelector: React.FC<EmailRecipientSelectorProps> = ({
  selectedRecipients,
  onRecipientsChange,
  suggestedGroups = [],
  onPendingChange,
  placeholder = 'Search by name or email',
  organizationIdentityId,
  maxRecipients,
}) => {
  const userId = useSelector((state: RootState) => state?.auth.userId)
  const [inputValue, setInputValue] = useState('')
  const [isFocused, setIsFocused] = useState(false)
  const [searchResults, setSearchResults] = useState<EmailAddressResult[]>([])
  const [commitsInFlight, setCommitsInFlight] = useState(0)
  const blurTimeoutRef = useRef<number | null>(null)
  const selectedRecipientsRef = useRef(selectedRecipients)
  const inFlightAddressesRef = useRef(new Set<string>())

  useEffect(() => {
    selectedRecipientsRef.current = selectedRecipients
  }, [selectedRecipients])

  useEffect(() => {
    return (): void => {
      if (blurTimeoutRef.current) {
        clearTimeout(blurTimeoutRef.current)
      }
      debouncedSearch.cancel()
    }
  }, [])

  const atLimit = maxRecipients != null && selectedRecipients.length >= maxRecipients

  const allSuggestions = useMemo(
    () => suggestedGroups.flatMap((g) => g.recipients),
    [suggestedGroups],
  )

  const availableOptions = useMemo(
    () => allSuggestions.filter((r) => !selectedRecipients.some((s) => s.address === r.address)),
    [allSuggestions, selectedRecipients],
  )

  const showSuggestions =
    isFocused && !atLimit && inputValue.length === 0 && availableOptions.length > 0

  useEffect(() => {
    onPendingChange?.(inputValue.trim().length > 0 || commitsInFlight > 0)
  }, [inputValue, commitsInFlight, onPendingChange])

  const debouncedSearch = useMemo(
    () =>
      debounce(async (query: string) => {
        if (!userId || !organizationIdentityId || query.length < 3) {
          setSearchResults([])
          return
        }
        try {
          const response = await apiZodios.contacts_searchEmails({
            user_id: userId,
            organization_identity_id: organizationIdentityId,
            query,
            limit: 10,
          })
          setSearchResults(response.results)
        } catch {
          setSearchResults([])
        }
      }, 200),
    [userId, organizationIdentityId],
  )

  useEffect(() => {
    return (): void => {
      debouncedSearch.cancel()
    }
  }, [debouncedSearch])

  const handleFocus = (): void => {
    if (blurTimeoutRef.current) {
      clearTimeout(blurTimeoutRef.current)
      blurTimeoutRef.current = null
    }
    setIsFocused(true)
    if (inputValue.trim().length >= 3) {
      debouncedSearch(inputValue.trim())
    }
  }

  const handleBlur = (): void => {
    const pending = inputValue.trim()
    blurTimeoutRef.current = window.setTimeout(() => {
      setIsFocused(false)
      setSearchResults([])
      if (isValidEmail(pending)) {
        void handleCommitFreeTyped(pending)
      }
    }, 200)
  }

  const handleSelectRecipient = useCallback(
    (recipient: EmailAddressResult) => {
      const current = selectedRecipientsRef.current
      const alreadySelected = current.some((r) => r.address === recipient.address)
      if (alreadySelected) return
      const next = [...current, recipient]
      const limited = maxRecipients != null ? next.slice(-maxRecipients) : next
      selectedRecipientsRef.current = limited
      onRecipientsChange(limited)
    },
    [onRecipientsChange, maxRecipients],
  )

  const handleCommitFreeTyped = useCallback(
    async (address: string) => {
      if (!isValidEmail(address)) return
      if (inFlightAddressesRef.current.has(address)) return
      inFlightAddressesRef.current.add(address)
      setInputValue('')
      setSearchResults([])
      setCommitsInFlight((count) => count + 1)
      try {
        if (userId) {
          const response = await apiZodios.contacts_obtainEmailAddress({
            user_id: userId,
            email: address,
          })
          handleSelectRecipient({
            id: response.email_address_id,
            address,
            contact_name: '',
          })
        } else {
          handleSelectRecipient({ id: address, address, contact_name: '' })
        }
      } catch {
        handleSelectRecipient({ id: address, address, contact_name: '' })
      } finally {
        inFlightAddressesRef.current.delete(address)
        setCommitsInFlight((count) => count - 1)
      }
    },
    [userId, handleSelectRecipient],
  )

  const filteredGroups = useMemo(() => {
    return suggestedGroups
      .map((group) => ({
        ...group,
        recipients: group.recipients.filter(
          (r) => !selectedRecipients.some((sel) => sel.address === r.address),
        ),
      }))
      .filter((group) => group.recipients.length > 0)
  }, [suggestedGroups, selectedRecipients])

  const showSearch = isFocused && !atLimit && inputValue.length >= 3

  return (
    <Box
      className={`internal-email-recipient-selector${
        atLimit ? ' internal-email-recipient-selector--at-limit' : ''
      }`}
    >
      <Autocomplete<EmailAddressResult, true, false, true>
        multiple
        freeSolo
        options={[]}
        value={selectedRecipients}
        onChange={(_event, newValue) => {
          const last = newValue[newValue.length - 1]
          if (typeof last === 'string') {
            handleCommitFreeTyped(last)
            return
          }
          const recipients = newValue as EmailAddressResult[]
          onRecipientsChange(maxRecipients != null ? recipients.slice(-maxRecipients) : recipients)
        }}
        inputValue={inputValue}
        onInputChange={(_event, value, reason) => {
          if (reason === 'reset') {
            setInputValue('')
            return
          }
          setInputValue(value)
          debouncedSearch(value)
        }}
        getOptionLabel={(option) => (typeof option === 'string' ? option : option.address)}
        renderValue={(value, getItemProps) =>
          value.map((recipient, index) => {
            const label =
              typeof recipient === 'string'
                ? recipient
                : recipient.contact_name && recipient.contact_name.trim()
                  ? recipient.contact_name
                  : recipient.address
            const { key, ...itemProps } = getItemProps({ index })
            return <Chip key={key} {...itemProps} label={label} size='small' />
          })
        }
        renderInput={({ slotProps, ...params }) => (
          <TextField
            {...params}
            variant='borderless'
            InputProps={{ ...slotProps.input }}
            inputProps={{
              ...slotProps.htmlInput,
              onFocus: (e) => {
                handleFocus()
                ;(slotProps.htmlInput as React.InputHTMLAttributes<HTMLInputElement>)?.onFocus?.(
                  e as React.FocusEvent<HTMLInputElement>,
                )
              },
              onKeyDown: (e) => {
                if (e.key === ' ') {
                  const pending = inputValue.trim()
                  if (isValidEmail(pending)) {
                    e.preventDefault()
                    void handleCommitFreeTyped(pending)
                  }
                }
                ;(slotProps.htmlInput as React.InputHTMLAttributes<HTMLInputElement>)?.onKeyDown?.(
                  e as React.KeyboardEvent<HTMLInputElement>,
                )
              },
              onBlur: (e) => {
                handleBlur()
                ;(slotProps.htmlInput as React.InputHTMLAttributes<HTMLInputElement>)?.onBlur?.(
                  e as React.FocusEvent<HTMLInputElement>,
                )
              },
            }}
            placeholder={atLimit ? undefined : placeholder}
          />
        )}
        open={false}
        disableClearable={false}
        className='internal-email-recipient-selector__autocomplete'
      />

      {showSearch && searchResults.length > 0 && (
        <Paper className='internal-email-recipient-selector__dropdown' elevation={4}>
          <Box className='internal-email-recipient-selector__group'>
            {searchResults
              .filter((r) => !selectedRecipients.some((s) => s.address === r.address))
              .map((recipient) => (
                <Box
                  key={recipient.address}
                  className='internal-email-recipient-selector__option'
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => {
                    handleSelectRecipient(recipient)
                    setInputValue('')
                    setSearchResults([])
                  }}
                >
                  <Box className='internal-email-recipient-selector__option-info'>
                    <Typography
                      variant='subtitle2'
                      color='textPrimary'
                      className='internal-email-recipient-selector__option-name'
                    >
                      {recipient.contact_name}
                    </Typography>
                    <Typography
                      color={
                        recipient.contact_name && recipient.contact_name?.trim()
                          ? 'textSecondary'
                          : 'textPrimary'
                      }
                      variant={
                        recipient.contact_name && recipient.contact_name?.trim()
                          ? 'caption'
                          : 'subtitle2'
                      }
                      className='internal-email-recipient-selector__option-email'
                    >
                      {recipient.address}
                    </Typography>
                  </Box>
                </Box>
              ))}
          </Box>
        </Paper>
      )}
      {showSuggestions && !showSearch && (
        <Paper className='internal-email-recipient-selector__dropdown' elevation={4}>
          {filteredGroups.map((group) => (
            <Box key={group.label} className='internal-email-recipient-selector__group'>
              <Typography
                color='textSecondary'
                variant='caption'
                className='internal-email-recipient-selector__group-label'
              >
                {group.label}
              </Typography>
              {group.recipients.map((recipient) => (
                <Box
                  key={recipient.address}
                  className='internal-email-recipient-selector__option'
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => {
                    handleSelectRecipient(recipient)
                    setInputValue('')
                    setSearchResults([])
                  }}
                >
                  <Avatar
                    size={typography.fontSizePx.xxxl}
                    alt={
                      recipient.contact_name && recipient.contact_name?.trim()
                        ? recipient.contact_name
                        : recipient.address
                    }
                  />
                  <Box className='internal-email-recipient-selector__option-info'>
                    <Typography
                      variant='subtitle2'
                      color='textPrimary'
                      className='internal-email-recipient-selector__option-name'
                    >
                      {recipient.contact_name}
                    </Typography>
                    <Typography
                      color={
                        recipient.contact_name && recipient.contact_name?.trim()
                          ? 'textSecondary'
                          : 'textPrimary'
                      }
                      variant={
                        recipient.contact_name && recipient.contact_name?.trim()
                          ? 'caption'
                          : 'subtitle2'
                      }
                      className='internal-email-recipient-selector__option-email'
                    >
                      {recipient.address}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          ))}
        </Paper>
      )}
    </Box>
  )
}

export default EmailRecipientSelector
